import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { DrawerBodyWrapper, DrawerFooterWrapper, TOAST_TYPE } from '@base'
import { Button as ButtonV2 } from '@baseV2'
import { enquiryInputType, strings } from '@constants'
import { getProductByCategory } from '@data'
import { showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { generateItemId, isBrandOptional } from '@utils'
import EnquiryItemForm from '@pages/Enquiry/EnquiryItemForm'
import { getDuplicateGradeRowIds } from '@pages/Enquiry/EnquiryItemForm/inputAdapters'
import { Devices, Spacings } from '@styles'


const initialFormState = {
	category: {
		label: strings('category'),
		disabled: false,
		readOnly: false,
		displayKey: 'category_name',
		primaryKey: 'category_id',
		placeholder: strings('select_a_category'),
		data: [],
		value: '',
		dirty: false,
		shouldFetchProducts: { flag: true },
	},
	product: {
		label: strings('product'),
		disabled: false,
		readOnly: false,
		displayKey: 'product_name',
		primaryKey: 'product_id',
		placeholder: strings('search_product'),
		data: [],
		value: '',
		dirty: false,
	},
	grade: {
		label: strings('grade'),
		placeholder: strings('enter', 'grade'),
		readOnly: false,
		value: '',
		dirty: false,
	},
	requiredWeight: {
		label: strings(['weight', '(', 'mt', ')']),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	targetPrice: {
		// SPO shows "Rate" instead of "Target Price".
		label: 'Rate',
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	brands: {
		label: strings('brands'),
		disabled: false,
		readOnly: false,
		displayKey: 'name',
		primaryKey: 'id',
		placeholder: strings('select_brands'),
		data: [],
		value: [],
		dirty: false,
	},
	message: {
		label: strings('message'),
		disabled: false,
		readOnly: false,
		value: '',
		dirty: false,
	},
	tcRequired: {
		label: strings('tc_reqd'),
		disabled: false,
		value: true,
		dirty: false,
	},
	inputType: {
		label: strings('type'),
		disabled: false,
		value: '',
		dirty: false,
	},
	hsn_code: {
		label: strings('hsn', 'code'),
		placeholder: strings('enter', 'hsn', 'code'),
		disabled: true,
		readOnly: false,
		value: '',
		dirty: false,
	},
	gst: {
		label: strings('gst'),
		placeholder: strings('enter', 'gst'),
		readOnly: true,
		value: '',
		dirty: false,
	},
}

const ActionButton = styled(ButtonV2)`
	margin-left: ${Spacings.SPACING_4B};
	max-width: 14rem;
	flex-grow: 1;

	&:first-of-type {
		margin-left: 0;
	}

	@media ${Devices.tablet} {
		flex-grow: 0;
	}
`

// Breathing room below the drawer header before the first field.
const FormWrapper = styled.div`
	padding-top: ${Spacings.SPACING_4B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
`

// SPO add-item drawer: reuses the common EnquiryItemForm (same UI/initial state as the enquiry
// order-item drawer). On "Add item" it converts each filled product row into the SPO order-item
// shape (allotted_weight / price_per_kg / brand_ordered / tc_available) — matching the old
// SPOItemForm's formStateToObject — and hands it to addNewItemCallback.
const AddItemsDrawer = ({ state, addNewItemCallback }) => {
	const globalDispatch = useDispatch()
	const user = useSelector((s) => s.getIn(['user', 'user']))
	const activeCommodity = user?.commodity?.find((c) => c?.active)
	const brandOptional = isBrandOptional(user)

	const categories = state?.categories
	const brands = state?.brands
	const defaultBrand = { name: 'ANY', id: 'any' }
	const checkTmtItem = ['ms_tmt_bars']

	const [formState, setFormState] = useState(initialFormState)
	const [productsLoading, setProductsLoading] = useState(false)
	const lastCategoryProductsFetched = useRef()

	const updateFormState = useCallback((key, updates) => {
		setFormState((old) => ({ ...old, [key]: { ...old[key], ...updates } }))
	}, [])

	// Feed the SPO reducer's categories / brands into the form.
	useEffect(() => {
		if (categories?.data?.data) updateFormState('category', { data: categories.data.data })
	}, [categories, updateFormState])

	useEffect(() => {
		if (brands?.data) updateFormState('brands', { data: brands.data })
	}, [brands, updateFormState])

	// Fetch products when the category changes (same flow as CreateItem).
	useEffect(() => {
		if (formState.category.dirty) {
			updateFormState('product', { value: '', data: [], tmtProduct: undefined, dirty: false })
		}
		const categoryId = formState.category.value?.category_id
		if (categoryId && categoryId !== lastCategoryProductsFetched.current) {
			lastCategoryProductsFetched.current = categoryId
			setProductsLoading(true)
			getProductByCategory(categoryId)
				.then((data) => updateFormState('product', { data, value: '' }))
				.finally(() => setProductsLoading(false))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formState.category.value, formState.category.shouldFetchProducts])

	// Already-added SPO items, mapped to the enquiry-item shape the form uses for duplicate display.
	const spoItemsJS = useMemo(() => {
		const items = state?.newSpo?.order_items?.toJS?.() ?? state?.newSpo?.order_items ?? []
		return (items ?? []).map((it) => ({
			...it,
			required_weight: it.allotted_weight,
			target_price: it.price_per_kg,
			brands: it.brand_ordered ? [it.brand_ordered] : [],
			tc_required: it.tc_available,
		}))
	}, [state?.newSpo?.order_items])

	// Base enquiry-item snapshot from the shared fields (product-specific fields are overridden per row).
	const baseEnquiryItem = () => ({
		category_id: formState.category.value.category_id,
		category_name: formState.category.value.category_name,
		input_type: formState.inputType.value,
		brands: formState.brands.value.map((a) => a.name),
		message: formState.message.value,
		tc_required: formState.tcRequired.value,
		hsn_code: formState?.hsn_code?.value,
	})

	// Convert an enquiry-shaped row into the SPO order-item shape (mirrors SPOItemForm.formStateToObject).
	// required_weight is already ×1000 for WEIGHT here, so it maps straight to allotted_weight.
	const toSpoItem = (e) => ({
		allotted_weight: e.required_weight,
		brand_ordered: e.brands?.[0],
		category_id: e.category_id,
		category_name: e.category_name,
		grade: e.grade === '' ? null : e.grade,
		hsn_code: e.hsn_code,
		input_type: e.input_type,
		price_per_kg: e.target_price,
		product_id: e.product_id,
		product_name: e.product_name,
		tc_available: e.tc_required,
		item_id: generateItemId(e.product_id, e.grade),
	})

	const addNewItemHandler = () => {
		if (formState?.product?.tmtProduct === undefined) {
			globalDispatch(showToast(true, strings('msg_wrong_value'), { type: TOAST_TYPE.ERROR }))
			return
		}
		const seen = new Set()
		formState.product.tmtProduct.forEach((item) => {
			if (item?.required_weight === '' || item?.required_weight == null) return
			const e = {
				...baseEnquiryItem(),
				product_id: item.product_id,
				product_name: item.product_name,
				required_weight:
					formState.inputType.value === enquiryInputType.WEIGHT ? parseFloat(item.required_weight * 1000) : item.required_weight,
				target_price: item.target_price,
				grade: item.grade,
				message: item.message,
			}
			const spoItem = toSpoItem(e)
			if (seen.has(spoItem.item_id)) return
			seen.add(spoItem.item_id)
			addNewItemCallback(spoItem)
		})
		globalDispatch(toggleDrawer(false))
	}

	// Same enable/disable rules as the enquiry add-item button.
	const addDisableButton = () => {
		if (!formState.category.value?.category_id) return true
		if (!brandOptional && !formState.brands.value.length) return true
		if (formState?.product?.tmtProduct?.length) {
			const reg = /^[\d]+(?:\.\d{1,3})?$/
			const rowsWithWeight = formState?.product?.tmtProduct?.filter((item) => item?.required_weight) ?? []
			if (!rowsWithWeight.length) return true
			if (getDuplicateGradeRowIds(formState?.product?.tmtProduct).size) return true
			// An SPO line is priced, so a row that carries a weight must carry a rate as well — those are
			// exactly the rows that get added.
			if (rowsWithWeight.some((item) => item?.target_price === '' || item?.target_price == null)) return true
			if (rowsWithWeight.some((item) => !reg.test(String(item?.target_price)))) return true
			return rowsWithWeight.find((item) => !reg.test(item?.required_weight))
		}
		if (formState?.product?.data.length && !formState?.product?.tmtProduct?.length) return true
		return false
	}

	return (
		<>
			<DrawerBodyWrapper>
				<FormWrapper>
					<EnquiryItemForm
						enquiryItems={spoItemsJS}
						itemId={null}
						state={formState}
						updateState={updateFormState}
						productsLoading={productsLoading}
						defaultBrand={defaultBrand}
						defualtCategoryId={null}
						checkTmtItem={checkTmtItem}
						activeCommodity={activeCommodity}
					/>
				</FormWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<ActionButton color='primary' disabled={addDisableButton()} onClick={addNewItemHandler}>
					{strings('add_item')}
				</ActionButton>
			</DrawerFooterWrapper>
		</>
	)
}

export default AddItemsDrawer
